import { useCallback } from "react";
import { toast } from "sonner";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useDispatch } from "react-redux";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import { StatusGroupType } from "../StatusesContent";
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from "../constants";
import { validateStatusName } from "../utils";
import { KanbanDefaultStates } from "../../ProjectList/CanvasKanban";
import { ProjectWithStatuses } from "@/utils/types";
import {
  migrateDefaultsToProjectStatuses,
  updateColumnOrder,
  getDefaultColumnConfig,
} from "../utils/columnConfigUtils";
import { findStatusByIdOrKey } from "../utils/statusNameUtils";
interface StatusOperationsHook {
  handleCreateStatus: (statusName: string, statusType: string) => Promise<void>;
  handleUpdateStatus: (statusId: string, newName: string) => Promise<void>;
  handleDeleteStatus: (statusId: string) => Promise<void>;
  handleReorderColumns: (
    newOrder: Array<{ key: string; isDefault: boolean }>,
  ) => Promise<void>;
}
interface UseStatusOperationsProps {
  projectData: ProjectWithStatuses;
  customProjectStatuses: ProjectStatus[];
  projectStatusGroups: StatusGroupType[];
}

export const useStatusOperations = ({
  projectData,
  customProjectStatuses,
  projectStatusGroups,
}: UseStatusOperationsProps): StatusOperationsHook => {
  const dispatch = useDispatch();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const updateFeaturePrdStatuses = useIssuesStore(
    (state) => state.updateFeaturePrdStatuses,
  );
  const updateUIStateWithNewStatuses = useIssuesStore(
    (state) => state.updateUIStateWithNewStatuses,
  );
  const issuesByProject = useIssuesStore((state) => state.issuesByProject);
  const fetchIssuesForProject = useIssuesStore(
    (state) => state.fetchIssuesForProject,
  );

  const handleCreateStatus = useCallback(
    async (statusName: string, statusType: string) => {
      try {
        const defaultStatusNames = Object.values(KanbanDefaultStates);
        const validation = validateStatusName(
          statusName,
          customProjectStatuses,
          undefined,
          defaultStatusNames,
        );
        if (!validation.isValid) {
          toast.error(validation.error);
          return;
        }

        if (!statusType.trim()) {
          toast.error(ERROR_MESSAGES.STATUS_TYPE_REQUIRED);
          return;
        }

        const newStatus: ProjectStatus = {
          id: crypto.randomUUID(),
          name: statusName.trim(),
          type: statusType,
          isDefault: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        if (projectData && supabaseClient) {
          const currentStatuses =
            (projectData as ProjectWithStatuses).statuses || [];
          const updatedStatuses = [...currentStatuses, newStatus];

          const optimisticProjectData = {
            ...projectData,
            statuses: updatedStatuses,
          };

          dispatch(
            onProjectsSnapshot({
              newItems: [optimisticProjectData as ProjectWithStatuses],
            }),
          );

          try {
            const response = await supabaseClient
              .from("projects")
              .update({ statuses: updatedStatuses })
              .eq("id", projectData.id)
              .select()
              .single();

            if (response.data) {
              dispatch(
                onProjectsSnapshot({
                  newItems: [response.data],
                }),
              );
              toast.success(SUCCESS_MESSAGES.STATUS_CREATED);
            }
          } catch (serverError) {
            dispatch(
              onProjectsSnapshot({
                newItems: [projectData],
              }),
            );
            throw serverError;
          }
        }
      } catch (error) {
        console.error("Error creating status:", error);
        toast.error(ERROR_MESSAGES.CREATE_STATUS_FAILED);
      }
    },
    [projectData, dispatch, supabaseClient],
  );

  const handleUpdateStatus = useCallback(
    async (statusId: string, newName: string) => {
      try {
        const defaultStatusNames = Object.values(KanbanDefaultStates);
        const validation = validateStatusName(
          newName,
          customProjectStatuses,
          statusId,
          defaultStatusNames,
        );
        if (!validation.isValid) {
          toast.error(validation.error);
          return;
        }

        const statusToUpdate = findStatusByIdOrKey(
          statusId,
          customProjectStatuses,
        );
        const isDefaultStatusKey =
          Object.keys(KanbanDefaultStates).includes(statusId);
        const isDefaultStatus = statusToUpdate?.isDefault || isDefaultStatusKey;
        const defaultStatusKey =
          statusToUpdate?.defaultStatusKey ||
          (isDefaultStatusKey ? statusId : null);

        let updatedStatuses: ProjectStatus[];
        let oldStatusName: string;
        const newStatusName = newName.trim();

        if (isDefaultStatus && defaultStatusKey) {
          if (statusToUpdate) {
            // Simple update for existing migrated default
            oldStatusName = statusToUpdate.name;

            if (oldStatusName === newStatusName) {
              return;
            }

            updatedStatuses = customProjectStatuses.map((status) =>
              status.isDefault &&
              (status.defaultStatusKey === defaultStatusKey ||
                status.id === statusId ||
                status.defaultStatusKey === statusId ||
                status.id === defaultStatusKey)
                ? {
                    ...status,
                    id: status.id.includes("-")
                      ? status.id
                      : crypto.randomUUID(),
                    defaultStatusKey: defaultStatusKey,
                    name: newStatusName,
                    updatedAt: new Date().toISOString(),
                  }
                : status,
            );
          } else {
            // First-time migration - ensure new name is preserved
            oldStatusName =
              KanbanDefaultStates[
                defaultStatusKey as keyof typeof KanbanDefaultStates
              ];
            updatedStatuses = migrateDefaultsToProjectStatuses(
              customProjectStatuses,
              defaultStatusKey,
              newStatusName, // CRITICAL: This must be the actual new name
            );
          }
        } else {
          if (!statusToUpdate) {
            toast.error(ERROR_MESSAGES.STATUS_NOT_FOUND);
            return;
          }

          oldStatusName = statusToUpdate.name;

          if (oldStatusName === newStatusName) {
            return;
          }

          updatedStatuses = customProjectStatuses.map((status) =>
            status.id === statusId
              ? {
                  ...status,
                  name: newStatusName,
                  updatedAt: new Date().toISOString(),
                }
              : status,
          );
        }

        if (projectData && supabaseClient) {
          try {
            await updateFeaturePrdStatuses(
              supabaseClient,
              projectData.id,
              oldStatusName,
              newStatusName,
            );

            const projectResponse = await supabaseClient
              .from("projects")
              .update({ statuses: updatedStatuses })
              .eq("id", projectData.id)
              .select()
              .single();

            if (projectResponse.data) {
              const projectIssues = issuesByProject[projectData.id];
              const issuesToUpdate = projectIssues
                ? Object.values(projectIssues).filter(Boolean)
                : [];

              updateUIStateWithNewStatuses(
                oldStatusName,
                newStatusName,
                issuesToUpdate,
              );

              if (!projectIssues && supabaseClient && fetchIssuesForProject) {
                fetchIssuesForProject(supabaseClient, projectData.id, false);
              }

              const optimisticProjectData = {
                ...projectData,
                statuses: updatedStatuses,
              };

              dispatch(
                onProjectsSnapshot({
                  newItems: [optimisticProjectData as ProjectWithStatuses],
                }),
              );

              dispatch(
                onProjectsSnapshot({
                  newItems: [projectResponse.data],
                }),
              );

              toast.success(SUCCESS_MESSAGES.STATUS_UPDATED);
            }
          } catch (error) {
            console.error("Error updating status:", error);

            const errorMessage =
              error instanceof Error
                ? error.message
                : ERROR_MESSAGES.UPDATE_STATUS_FAILED;
            toast.error(errorMessage);

            dispatch(
              onProjectsSnapshot({
                newItems: [projectData],
              }),
            );
          }
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error(ERROR_MESSAGES.UPDATE_STATUS_FAILED);
      }
    },
    [
      customProjectStatuses,
      projectData,
      dispatch,
      supabaseClient,
      updateFeaturePrdStatuses,
      updateUIStateWithNewStatuses,
      issuesByProject,
      fetchIssuesForProject,
    ],
  );

  const handleDeleteStatus = useCallback(
    async (statusId: string) => {
      try {
        const statusToDelete = customProjectStatuses.find(
          (status) => status.id === statusId,
        );

        if (!statusToDelete) {
          toast.error(ERROR_MESSAGES.STATUS_NOT_FOUND);
          return;
        }

        const statusInUse = projectStatusGroups.some((group) =>
          group.statuses.some(
            (status) => status.id === statusId && status.count > 0,
          ),
        );

        if (statusInUse) {
          let canvasCount = 0;
          projectStatusGroups.forEach((group) => {
            group.statuses.forEach((status) => {
              if (status.id === statusId) {
                canvasCount = status.count;
              }
            });
          });

          toast.error(
            `${ERROR_MESSAGES.STATUS_IN_USE} "${
              statusToDelete.name
            }" - it's in use by ${canvasCount} canvas${
              canvasCount === 1 ? "" : "es"
            }`,
          );
          return;
        }

        if (projectData && supabaseClient) {
          try {
            const { data: canvases } = await supabaseClient
              .from("canvases")
              .select("id")
              .eq("project_id", projectData.id);

            if (canvases && canvases.length > 0) {
              const { data: featurePrds } = await supabaseClient
                .from("feature_prd")
                .select("id")
                .eq("status", statusToDelete.name)
                .in(
                  "canvas_id",
                  canvases.map((c) => c.id),
                );

              if (featurePrds && featurePrds.length > 0) {
                toast.error(
                  `${ERROR_MESSAGES.STATUS_IN_USE} "${statusToDelete.name}" - it's still in use by feature PRDs`,
                );
                return;
              }
            }
          } catch (error) {
            toast.error("Unable to validate status usage. Please try again.");
            return;
          }
        }

        const updatedStatuses = customProjectStatuses.filter(
          (status) => status.id !== statusId,
        );

        if (projectData && supabaseClient) {
          const response = await supabaseClient
            .from("projects")
            .update({ statuses: updatedStatuses })
            .eq("id", projectData.id)
            .select()
            .single();

          if (response.data) {
            dispatch(
              onProjectsSnapshot({
                newItems: [response.data],
              }),
            );
            toast.success(SUCCESS_MESSAGES.STATUS_DELETED);
          }
        }
      } catch (error) {
        console.error("Error deleting status:", error);
        toast.error(ERROR_MESSAGES.DELETE_STATUS_FAILED);
      }
    },
    [
      customProjectStatuses,
      projectData,
      dispatch,
      supabaseClient,
      projectStatusGroups,
    ],
  );

  const handleReorderColumns = useCallback(
    async (newOrder: Array<{ key: string; isDefault: boolean }>) => {
      try {
        if (!projectData || !supabaseClient) {
          return;
        }

        const currentStatuses = customProjectStatuses || [];
        let statusesToReorder = [...currentStatuses];
        const defaultConfig = getDefaultColumnConfig();

        defaultConfig.forEach((defaultCol) => {
          const exists = statusesToReorder.some(
            (s) =>
              s.isDefault &&
              (s.id === defaultCol.key || s.name === defaultCol.name),
          );
          if (!exists) {
            statusesToReorder = migrateDefaultsToProjectStatuses(
              statusesToReorder,
              defaultCol.key,
            );
          }
        });

        const updatedStatuses = updateColumnOrder(statusesToReorder, newOrder);

        const optimisticProjectData = {
          ...projectData,
          statuses: updatedStatuses,
        };

        dispatch(
          onProjectsSnapshot({
            newItems: [optimisticProjectData as ProjectWithStatuses],
          }),
        );

        try {
          const projectResponse = await supabaseClient
            .from("projects")
            .update({ statuses: updatedStatuses })
            .eq("id", projectData.id)
            .select()
            .single();

          if (projectResponse.data) {
            dispatch(
              onProjectsSnapshot({
                newItems: [projectResponse.data],
              }),
            );
            toast.success("Column order updated");
          }
        } catch (error) {
          console.error("Error reordering columns:", error);
          toast.error("Failed to update column order");

          dispatch(
            onProjectsSnapshot({
              newItems: [projectData],
            }),
          );
        }
      } catch (error) {
        console.error("Error reordering columns:", error);
        toast.error("Failed to update column order");
      }
    },
    [customProjectStatuses, projectData, dispatch, supabaseClient],
  );

  return {
    handleCreateStatus,
    handleUpdateStatus,
    handleDeleteStatus,
    handleReorderColumns,
  };
};
